import React from 'react';
import { theme } from '../../../../themes';
import { Typography } from '@mui/material';
import { MUIDataTableOptions } from 'mui-datatables';
import { StrategyAssociationItem } from '../store/types';
import { LoadingProgress } from '../../../../common/store/types';
import { DateTimeFormat, formatDollars, getLocalDateTimeNow } from '../../../../common';
import { DatatableColumn, FilterDataType } from '../../../../common/components/dataTable/types';
import { ClientSideDataTable } from '../../../../common/components/dataTable/clientSide/clientSideDataTable';

export interface Props {
  progress: LoadingProgress;
  data: StrategyAssociationItem[];
}
export function AssociationsTable({ progress, data }: Props): JSX.Element {
  const defaultItems: StrategyAssociationItem[] = [];
  const columns: DatatableColumn[] = [
    {
      label: 'AFSL NAME',
      name: 'afslName',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography
              variant={'h5'}
              color={'primary'}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {data[dataIndex] !== undefined ? data[dataIndex].afslName : ''}
            </Typography>
          );
        },
        sort: false,
        viewColumns: true,
      },
    },
    {
      label: 'PRACTICE NAME',  
      name: 'practiceName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'ADVISER NAME',  
      name: 'adviserName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'CLIENT ID',
      name: 'clientId',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography
              variant={'h5'}
              color={'primary'}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {data[dataIndex] !== undefined ? data[dataIndex].clientId : ''}
            </Typography>
          );
        },
        sort: false,
        viewColumns: true,
      },
    },
    {
      label: 'CLIENT NAME',  
      name: 'clientName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'PORTFOLIO ID',
      name: 'investmentServiceCode',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <Typography
              variant={'h5'}
              color={'primary'}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
            >
              {data[dataIndex] !== undefined ? data[dataIndex]?.investmentServiceCode : ''}
            </Typography>
          );
        },
        sort: false,
        viewColumns: true,
      },
    },
    {
      name: 'clientName',
      label: 'CLIENT AND PORTFOLIO NAME',
      textAlign: 'left',
      options: {
        customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
          return (
            <>
              <Typography variant={'h5'} align="left">
                {data[dataIndex] !== undefined ? data[dataIndex]?.clientName : ''}
              </Typography>
              <Typography color={'textSecondary'} variant={'h6'} align="left">
                {data[dataIndex] !== undefined ? data[dataIndex].investmentServiceName : ''}
              </Typography>
            </>
          );
        },
        download: false,     
        viewColumns: true,
        sort: true,
      },
      filterDataType: FilterDataType.string,
    },
    {
      label: 'PORTFOLIO NAME',  
      name: 'portfolioName',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      label: 'STRATEGY CODE',  
      name: 'strategyCode',    
      options: {
        display: 'excluded',
        download: true,     
      },
    },
    {
      filterDataType: FilterDataType.string,
      textAlign: 'right',
      label: 'VALUE',
      name: 'value',
      options: {
         customBodyRenderLite: function customBodyRenderLite(dataIndex): React.ReactNode {
            return <Typography variant="h5">{formatDollars(data[dataIndex]?.value)}</Typography>;
         },
         sort: false,
        viewColumns: true,
      },
      },
  ];

  const timestamp = getLocalDateTimeNow(DateTimeFormat.Filename);
  const options: MUIDataTableOptions = {
    search: true,
    download: true,
    filter: false,
    downloadOptions: {
      filename: `StrategyExposure_${timestamp}.csv`,
      separator: ','
    }
  };

  const tableData = (data && data.length > 0 && !progress.isLoading ? data : defaultItems).map(item => ({
    ...item,
    practiceName: item.practiceName,
    adviserName: item.adviserName,
    clientName: item.clientName,
    portfolioName: item.investmentServiceName,
    strategyCode: item.strategyCode,
    accountName: item.accountName,
    unitPrice: item.unitPrice,
    units: item.units,
    afslName: item.afslName,
    value: item.value
  }));

  return (
    <ClientSideDataTable
      data={tableData}
      columns={columns}
      options={options}
      loadingProgress={progress}
    />
  );
}
